import { FC, ReactNode } from 'react';
import {
  ActionMenuItemConfig,
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { useDuplicateTunnelDialog } from './duplicate/useDuplicateTunnelDialog';
import { useDeleteTunnelDialog } from './delete/useDeleteTunnelDialog';
import { TunnelActionTarget } from './tunnelActionTarget';

interface TunnelActionsMenuProps {
  tunnel: TunnelActionTarget;
  /** Après duplication : rafraîchir la liste, où la copie apparaît inactive. */
  onDuplicated?: () => void;
  /** Après suppression : rafraîchir la liste, ou quitter la fiche du tunnel supprimé. */
  onDeleted?: () => void;
  /**
   * Actions propres à l'écran appelant (ex. l'historique d'audit sur la fiche), insérées
   * entre la duplication et la suppression.
   */
  extraItems?: ActionMenuItemConfig[];
  /** Déclencheur alternatif (en-tête de fiche) ; sinon le bouton rond « ⋯ ». */
  trigger?: ReactNode;
}

/**
 * Menu « ⋯ » des actions d'un tunnel.
 *
 * Duplication et suppression sont rangées ici plutôt qu'affichées en boutons : la colonne
 * d'actions de la liste en comptait déjà trois, et ce sont deux gestes qu'on ne veut pas
 * déclencher d'un clic distrait. Les permissions conditionnent chaque entrée — sans
 * aucune, le menu disparaît.
 */
export const TunnelActionsMenu: FC<TunnelActionsMenuProps> = ({
  tunnel,
  onDuplicated,
  onDeleted,
  extraItems = [],
  trigger
}) => {
  const { createMenuItem } = useActionMenu();
  const { canDuplicate, openDuplicateDialog } = useDuplicateTunnelDialog();
  const { canDelete, openDeleteDialog } = useDeleteTunnelDialog();

  const items: ActionMenuItemConfig[] = [];

  if (canDuplicate) {
    items.push(
      createMenuItem('duplicate', 'Dupliquer', () => openDuplicateDialog(tunnel, onDuplicated), {
        icon: 'copy'
      })
    );
  }

  items.push(...extraItems);

  if (canDelete) {
    items.push(
      createMenuItem('delete', 'Supprimer', () => openDeleteDialog(tunnel, onDeleted), {
        icon: 'trash',
        variant: 'danger'
      })
    );
  }

  if (items.length === 0) return null;

  return <DropdownButton items={items} trigger={trigger} anchor="bottom" width="w-56" />;
};

export default TunnelActionsMenu;
